import React from 'react';
import type { AqiSummary } from './types';

const RADIUS        = 44;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

function aqiToOffset(aqi: number, max = 200): number {
  return CIRCUMFERENCE * (1 - Math.min(aqi / max, 1));
}

interface Props {
  airQuality: AqiSummary;
}

export default function AirQualityPanel({ airQuality }: Props) {
  const { aqi, status, statusColor, particulates } = airQuality;
  const offset = aqiToOffset(aqi);

  return (
    <div style={styles.panel}>
      <p style={styles.title}>Air Pollution</p>
      <p style={styles.sub}>Realtime</p>

      <div id='air-quality-index-panel' style={styles.gaugeWrap}>
        {/* Ring gauge */}
        <div style={styles.ringWrap}>
          <svg viewBox="0 0 110 110" width={110} height={110}
            role="img" aria-label={`Air quality index ${aqi} — ${status}`}>
            <circle cx={55} cy={55} r={RADIUS}
              stroke="var(--color-border-tertiary, rgba(0,0,0,0.1))"
              strokeWidth={8} fill="none" />
            <circle cx={55} cy={55} r={RADIUS}
              stroke={statusColor} strokeWidth={8} fill="none"
              strokeDasharray={CIRCUMFERENCE}
              strokeDashoffset={offset}
              strokeLinecap="round"
              transform="rotate(-90 55 55)"
              style={{ transition: 'stroke-dashoffset 0.8s ease' }} />
          </svg>
          <div style={styles.ringCenter}>
            <span style={styles.aqiNum}>{aqi}</span>
            <span style={{ ...styles.aqiStatus, color: statusColor }}>
              {status.toUpperCase()}
            </span>
          </div>
        </div>

        {/* Particulate rows */}
        <div style={styles.particulateList}>
          {particulates.map(p => (
            <div key={p.name} style={styles.particulateRow}>
              <span style={styles.particulateName}>{p.name}</span>
              <div style={styles.barTrack}
                role="progressbar"
                aria-valuenow={p.pct} aria-valuemin={0} aria-valuemax={100}
                aria-label={`${p.name} ${p.value} ${p.unit}`}>
                <div style={{ ...styles.barFill, width: `${p.pct}%`, background: p.color }} />
              </div>
              <span style={styles.particulateVal}>{p.value} {p.unit}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  panel:         { background: 'var(--color-bg-secondary, #f5f5f3)', borderRadius: 12, padding: '1.1rem 1.25rem', fontFamily: "'Syne', sans-serif" },
  title:         { fontSize: 13, fontWeight: 600, color: 'var(--color-text-primary, #2C2C2A)', margin: '0 0 0.15rem' },
  sub:           { fontFamily: "'DM Mono', monospace", fontSize: 11, color: 'var(--color-text-secondary, #5F5E5A)', margin: '0 0 1rem' },
  gaugeWrap:     { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem' },
  ringWrap:      { position: 'relative', width: 110, height: 110, flexShrink: 0 },
  ringCenter:    { position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' },
  aqiNum:        { fontFamily: "'DM Mono', monospace", fontSize: 24, fontWeight: 400, color: 'var(--color-text-primary, #2C2C2A)', lineHeight: 1 },
  aqiStatus:     { fontSize: 10, fontWeight: 600, marginTop: 2, letterSpacing: '0.04em' },
  particulateList: { display: 'flex', flexDirection: 'column', gap: 7, width: '100%' },
  particulateRow:  { display: 'flex', alignItems: 'center', gap: 8 },
  particulateName: { fontFamily: "'DM Mono', monospace", fontSize: 11, color: 'var(--color-text-secondary, #5F5E5A)', width: 48, flexShrink: 0 },
  barTrack:      { flex: 1, height: 4, background: 'var(--color-border-tertiary, rgba(0,0,0,0.1))', borderRadius: 2, overflow: 'hidden' },
  barFill:       { height: '100%', borderRadius: 2, transition: 'width 0.6s ease' },
  particulateVal:  { fontFamily: "'DM Mono', monospace", fontSize: 11, color: 'var(--color-text-primary, #2C2C2A)', fontWeight: 500, width: 72, textAlign: 'right', flexShrink: 0 },
};
